import EmailIcon from '@mui/icons-material/Email';
import {
  Button,
  ButtonGroup,
  ClickAwayListener,
  MenuItem,
  MenuList,
  Paper,
  Popper,
  Stack,
  Tooltip,
} from '@mui/material';
import { Dispatch, FC, SetStateAction, useRef, useState } from 'react';

import { Application } from '@features/applications';
import ScheduleMessageModal from '@features/messaging/components/ScheduleMessageModal/ScheduleMessageModal';
import { useGetThread } from '@features/messaging/hooks/useGetThread';
import { useSendMessage } from '@features/messaging/hooks/useSendMessage';
import { useUnreadMessages } from '@features/messaging/hooks/useUnreadMessages';
import { LoadingButton } from '@mui/lab';
import { toast } from 'react-toastify';
import { getStrippedText } from '@utils/helper';
import AssistantIcon from '@mui/icons-material/Assistant';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { AITooltipText } from '@features/messaging/constants';

type SendMessageButtonsProps = {
  setErrorText: Dispatch<SetStateAction<string>>;
  setShowAiPromptModal?: Dispatch<SetStateAction<boolean>>;
  message: string;
  threadId?: number;
  recipientId: number | undefined;
  company_id?: number | undefined;
  application?: Application;
  onSendSuccess: () => void;
  file: File | null;
  onUnreadSuccess: () => void;
  showMarkReadButton?: boolean;
  disabledUnreadButton?: boolean;
  isApplicant?: boolean | null;
};

const SendMessageButtons: FC<SendMessageButtonsProps> = ({
  setErrorText,
  setShowAiPromptModal,
  disabledUnreadButton,
  showMarkReadButton,
  message,
  threadId,
  application,
  recipientId,
  onSendSuccess,
  file,
  company_id,
  onUnreadSuccess,
  isApplicant,
}) => {
  const [isSendMenuOpen, setIsSendMenuOpen] = useState(false);
  const [isScheduleMessageModalOpen, setIsScheduleMessageModalOpen] =
    useState(false);
  const sendButtonRef = useRef<HTMLDivElement>(null);

  const { sendMessage, isSendingMessage } = useSendMessage({ setErrorText });
  const { thread } = useGetThread({
    applicantId: application?.applicant?.id,
    company_id: application?.job_listing?.company?.id,
    setErrorText,
  });

  const { unreadMessages } = useUnreadMessages({ setErrorText });

  return (
    <Stack spacing={2} direction={{ xs: 'column', sm: 'row' }}>
      <ButtonGroup
        variant="contained"
        color="primary"
        ref={sendButtonRef}
        disabled={
          !message || getStrippedText(message).length === 0 || isSendingMessage
        }
        sx={{ flexGrow: 1 }}
      >
        <LoadingButton
          fullWidth
          loading={isSendingMessage}
          onClick={() =>
            sendMessage(
              {
                message,
                application_id: application?.id,
                company_id: application?.job_listing?.company?.id ?? company_id,
                ...(!isApplicant && { recipient_id: recipientId! }),
                message_doc: file || undefined,
              },
              { onSuccess: onSendSuccess },
            )
          }
        >
          Send Message
        </LoadingButton>
        {/*
      // TODO: finish schedule message logic when backend is ready
      <Button size="small" onClick={() => setIsSendMenuOpen(true)}>
          <ArrowDropDownIcon />
        </Button> */}
      </ButtonGroup>

      {threadId && !isApplicant && (
        <Button
          color="secondary"
          variant="outlined"
          disabled={disabledUnreadButton && !showMarkReadButton}
          startIcon={<EmailIcon />}
          onClick={() =>
            unreadMessages(
              { thread_id: threadId },
              {
                onSuccess: (data) => {
                  if (data.status === 200) {
                    onUnreadSuccess();
                    toast.success(data.message);
                  } else if (data.status === 400) {
                    toast.error(data.message);
                  }
                },
              },
            )
          }
        >
          Mark Unread
        </Button>
      )}
      {setShowAiPromptModal && !isApplicant && (
        <Stack spacing={2} direction="row" alignItems="center">
          <Button
            variant="contained"
            onClick={() => setShowAiPromptModal(true)}
            startIcon={<AssistantIcon />}
          >
            Write with AI
          </Button>
          <Tooltip title={AITooltipText} placement="top" arrow>
            <HelpOutlineIcon color="secondary" fontSize="small" />
          </Tooltip>
        </Stack>
      )}
      <Popper
        open={isSendMenuOpen}
        anchorEl={sendButtonRef.current}
        disablePortal
        placement="top-end"
      >
        <Paper>
          <ClickAwayListener onClickAway={() => setIsSendMenuOpen(false)}>
            <MenuList>
              <MenuItem onClick={() => setIsScheduleMessageModalOpen(true)}>
                Schedule Message
              </MenuItem>
            </MenuList>
          </ClickAwayListener>
        </Paper>
      </Popper>
      <ScheduleMessageModal
        isOpen={isScheduleMessageModalOpen}
        handleClose={() => setIsScheduleMessageModalOpen(false)}
      />
    </Stack>
  );
};

export default SendMessageButtons;
